<template>
  <div v-if="showPopup" ref="target" class="popup py-0" 
  :class="{ 'mobile-popup-1': isMobile }">
    <spinner v-if="loading" />
    <div
      v-if="!loading"
      class="popup-header backgroundBalance py-2 d-flex align-items-center"
    >
      <div class="row align-items-center wrapper">
        <div class="col-6 column with-right-border">
          <h3 class="m-0 d-inline font-weight-semibold font-white">
            {{ balanceDisplaySign(userStore.user.displayBalance) }}${{
              roundToFixed2(Math.abs(userStore.user.displayBalance))
            }}
          </h3>
          <h5 class="m-0 font-weight-normal">BALANCE</h5>
        </div>
        <div class="col-6 column">
          <h3 class="m-0 d-inline font-weight-semibold font-white">
            ${{ noDecimal(available) }}
          </h3>
          <h5 class="m-0 font-weight-normal">AVAILABLE</h5>
        </div>
      </div>
    </div>
    <div class="popup-menu">
      <router-link
        v-if="!isMobile"
        to="/my-bets/active-wagers"
        class="router-link-style"
        @click.stop="emit('closePopUp')"
      >
        <font-awesome-icon
          :icon="faClock"
          class="mr-3 ml-0 icon-size"
          @click.stop="emit('closePopUp')"
        />
        <span>Pending Bets</span>
      </router-link>

      <router-link
        :to="{ name: 'AccountPage' }"
        class="router-link-style"
        @click.stop="emit('closePopUp')"
      >
        <font-awesome-icon :icon="faUserGear" class="mr-3 ml-0 icon-size" />
        <span>My Account</span>
      </router-link>


      <router-link
        v-if="canSeeRebates(userStore.user.username)"
        to="/my-rebates"
        class="router-link-style"
        @click.stop="emit('closePopUp')"
      >
        <font-awesome-icon :icon="faPercent" class="mr-3 ml-0 icon-size" />
        <span>Rebates</span>
      </router-link>

      <router-link
        v-if="!userStore.user.yesNoSummary"
        :to="{ name: 'InviteFriends' }"
        class="router-link-style"
        @click.stop="emit('closePopUp')"
      >
        <font-awesome-icon :icon="faUserPlus" class="mr-3 ml-0 icon-size" />
        <span>Invite Friends</span>
      </router-link>

      <!-- PAUSE ORDERS FUNCTIONALITY - COMMENTED OUT
      <div class="router-link-style" @click="managePause">
        <font-awesome-icon
          :icon="miscStore.pausedOffers !== 'resume' ? faPause : faPlay"
          class="mr-3 ml-0 icon-size"
        />
        <span v-if="miscStore.pausedOffers !== 'resume'">Pause Offers</span>
        <span v-else>View Paused Orders</span>
      </div>
      -->

      <router-link
        v-if="userStore.user.yesNoSummary"
        to="/affiliate-summary"
        class="router-link-style"
        @click.stop="emit('closePopUp')"
      >
        <font-awesome-icon :icon="faUserPlus" class="mr-3 ml-0 icon-size" />
        <span>Affiliate Summary</span>
      </router-link>

      <router-link
        v-if="userStore.user.seeCombined && !isMobile"
        to="/combined-bets/pending"
        class="router-link-style"
        @click.stop="emit('closePopUp')"
      >
        <font-awesome-icon :icon="faFileLines" class="mr-3 ml-0 icon-size" />
        <span>Combined Bets</span>
      </router-link>

      <router-link
        v-if="userStore.user.hasMarketMakerAccess"
        to="/marketmaker-my-bets/active-wagers"
        class="router-link-style"
      >
        <font-awesome-icon
          :icon="faLifeRing"
          class="mr-3 ml-0 font-weight-light icon"
        />
        <span>Marketmaker My Bets</span>
      </router-link>

      <router-link
        v-if="userStore.user.hasMonkeyAccess"
        to="/admin-monkeymaker"
        class="router-link-style"
        @click.stop="emit('closePopUp')"
      >
        <font-awesome-icon :icon="faCartShopping" class="mr-3 ml-0 icon-size" />
        <span>Monkey Machine</span>
      </router-link>

      <router-link
        v-if="userStore.user.hasMonkeyAccess"
        to="/circle-game"
        class="router-link-style"
        @click.stop="emit('closePopUp')"
      >
        <font-awesome-icon :icon="faBan" class="mr-3 ml-0 icon-size" />
        <span>Circle Game</span>
      </router-link>

      <a v-if="isMobile" href="#" class="router-link-style mt-0" @click.prevent="openSupport()">
        <font-awesome-icon
          :icon="faHeadset"
          class="mr-3 ml-0 icon-size"
        />
        <span>Support</span>
      </a>

      <a v-if="isMobile" href="#" class="router-link-style mt-0" @click="cancelAll()">
        <font-awesome-icon
          :icon="faTrashCan"
          class="mr-3 ml-0 icon-size-blue"
        />
        <span class="text-blue">Cancel All Offers</span>
      </a>

      <a v-if="!isMobile" href="#" class="router-link-style mt-0" @click="logout()">
        <font-awesome-icon
          :icon="faArrowRightFromBracket"
          class="mr-3 ml-0 icon-size-blue"
        />
        <span class="text-blue">Logout</span>
      </a>

      <a v-if="isMobile" href="#" class="router-link-style mobile-logout mt-2" @click="logout()">
        <font-awesome-icon
          :icon="faArrowRightFromBracket"
          class="mr-3 ml-0 icon-size-red"
        />
        <span class="text-red">Logout</span>
      </a>
    </div>
  </div>
</template>

<script setup>
// core
import Swal from 'sweetalert2';
import { ref, computed, onMounted } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useAPI } from '@/composables/useAPI.js';
import { useUserStore } from '@/stores/user';
import { useExchangeStore } from '@/stores/exchange';
import { useMiscStore } from '@/stores/store';
import { emitter } from '@/core/emitter';
import { useRouter, useRoute } from 'vue-router';
import { balanceDisplaySign, noDecimal, roundToFixed2 } from '@/core/utils';
import { canSeeRebates } from '@/core/rebateUtils';
import { PostHogUtils } from '@/core/PostHogUtils';
// PAUSE ORDERS NOTIFICATIONS - COMMENTED OUT
// import {
//   noOrderToPause,
//   ordersPausedSuccess,
//   ordersPauseError,
//   checkPausedOrdersError,
// } from '@/core/notifications';
// Components
import Spinner from '@/components/global/Spinner.vue';
import { useWindowSize } from '@/composables/useWindowSize';
// icons
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faUserGear } from '@fortawesome/pro-light-svg-icons/faUserGear';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { faUserPlus } from '@fortawesome/pro-light-svg-icons/faUserPlus';
import { faCartShopping } from '@fortawesome/pro-light-svg-icons/faCartShopping';
import { faLifeRing } from '@fortawesome/pro-light-svg-icons/faLifeRing';
import { faFileLines } from '@fortawesome/pro-light-svg-icons/faFileLines';
import { faArrowRightFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowRightFromBracket';
import { faPercent } from '@fortawesome/pro-light-svg-icons/faPercent';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';
import { faHeadset } from '@fortawesome/pro-light-svg-icons/faHeadset';
// PAUSE ORDERS ICONS - COMMENTED OUT
// import { faPause } from '@fortawesome/pro-light-svg-icons/faPause';
// import { faPlay } from '@fortawesome/pro-light-svg-icons/faPlay';
import { faSeedling } from '@fortawesome/pro-solid-svg-icons/faSeedling';
import { faBan } from '@fortawesome/free-solid-svg-icons/faBan';
import { storeToRefs } from 'pinia';

import { inject } from 'vue';
const ignoreElement = inject('ignoreElement', false);
// Cancel all offers functionality
const { request: cancelAllRequest } = useAPI('/session/cancelAllOrders');
const { request: cancelAllForLeagueRequest } = useAPI(
  '/session/cancelAllForLeague',
);
const { width } = useWindowSize();
// on click outside close popup
const emit = defineEmits(['closePopUp']);
const target = ref(null);
const loading = ref(false);
onClickOutside(target, (event) => emit('closePopUp'), {
  ignore: ignoreElement,
});
const isMobile = computed(() => {
  return width.value < 768;
});
const props = defineProps({
  showPopup: {
    type: Boolean,
    required: true,
  },
});

// init user store
const userStore = useUserStore();
const miscStore = useMiscStore();
// log user out
const router = useRouter();
const route = useRoute();
const { request: logOutRequest } = useAPI('/user/logout');
// PAUSE ORDERS API CALLS - COMMENTED OUT
// const { request: checkPausedOrders } = useAPI('session/checkForPausedOrders');
// const { request: pauseOrdersRequest } = useAPI('session/pauseOrders');

async function logout() {
  try {
    await logOutRequest();
    // Ordering is load-bearing: 'userLogOut' runs resetAllStores() ->
    // userStore.reset() -> PostHogUtils.reset(), which opts the device out.
    // Anything captured after that emit is silently dropped, so this has to
    // sit between the confirmed API call and the emit.
    PostHogUtils.capture('logged_out');
    emitter.emit('userLogOut');
    emit('closePopUp');
  } catch (error) {
    console.error('Logout failed ', error);
  }
}

function openSupport() {
  emit('closePopUp');
  // Open Intercom messenger after a brief delay to allow popup to close
  setTimeout(() => {
    // Ensure Intercom is loaded first
    if (window.loadIntercom) {
      window.loadIntercom();
    }
    if (window.Intercom) {
      window.Intercom('show');
    }
  }, 100);
}

const available = computed(() => {
  return (
    parseFloat(userStore.user.displayBalance) +
    parseFloat(userStore.user.liability) -
    parseFloat(userStore.user.creditLimit)
  );
});

async function cancelAll() {
  let showCancelLeague = false;
  let confirmText;
  if (
    route.params.league &&
    route.params.league !== 'all' &&
    route.params.league !== 'upcoming'
  ) {
    showCancelLeague = true;
    confirmText = `Cancel ${route.params.league.toUpperCase()}`;
  }

  const result = await Swal.fire({
    html: '<h4>Cancel Offers?</h4>',
    showCancelButton: true,
    showDenyButton: true,
    allowEnterKey: false,
    showConfirmButton: showCancelLeague,
    confirmButtonText: confirmText,
    denyButtonText: 'Cancel all',
    cancelButtonText: "Don't Cancel",
    confirmButtonColor: 'primary',
    denyButtonColor: '#6f75b1',
  });

  if (result.isConfirmed || result.isDenied) {
    loading.value = true;
    if (result.isDenied) {
      try {
        const response = await cancelAllRequest();
        loading.value = false;
        emitter.emit('showAlert', {
          type: 'canceled-order',
          shown: true,
          status: response.length > 0 ? 'ok' : 'warning',
          title:
            response.length > 0 ? 'All Orders Cancelled' : 'No Open Orders',
          detail: '',
        });
      } catch (error) {
        loading.value = false;
        emitter.emit('showAlert', {
          shown: true,
          status: 'danger',
          title: 'Orders not cancelled',
          detail: 'error cancelling orders',
        });
      }
    } else if (result.isConfirmed) {
      let params;
      if (route.params.sport === 'fighting') {
        const league =
          route.params.league === 'ufc'
            ? 'UFCMMA'
            : route.params.league.toUpperCase();
        params = {
          league: league,
        };
      } else {
        params = {
          league: route.params.league.toUpperCase(),
        };
      }
      try {
        cancelAllForLeagueRequest(params);
        loading.value = false;
        emitter.emit('showAlert', {
          type: 'canceled-order',
          shown: true,
          status: 'ok',
          title: 'Cancel Orders Received',
          detail: '',
        });
      } catch (error) {
        loading.value = false;
        emitter.emit('showAlert', {
          shown: true,
          type: 'warn',
          title: 'Orders not cancelled',
          detail: '',
        });
      }
    }
    Swal.fire('Confirmed!', 'Your orders are being cancelled.', 'success');
  }
}

// PAUSE ORDERS FUNCTION - COMMENTED OUT
// async function pauseOrders() {
//   const result = await Swal.fire({
//     html: '<h4>Are you sure you want to pause all orders?</h4>',
//     showCancelButton: true,
//     confirmButtonColor: 'primary',
//     denyButtonColor: '#6f75b1',
//     confirmButtonText: 'Yes',
//   });
//   if (result.isConfirmed) {
//     try {
//       loading.value = true;
//       const response = await pauseOrdersRequest();
//       if (!response.pausedOrders) {
//         noOrderToPause();
//       } else {
//         ordersPausedSuccess();
//       }
//       miscStore.setPausedOffers(response.pausedOrders || 'resume');
//     } catch (error) {
//       console.log(error);
//       ordersPauseError();
//     } finally {
//       loading.value = false;
//     }
//   }
// }

// CHECK PAUSED OFFERS FUNCTION - COMMENTED OUT
// async function checkPausedOffers() {
//   try {
//     const response = await checkPausedOrders();
//     miscStore.setPausedOffers(response.pausedOrders);
//     return true;
//   } catch (error) {
//     console.error('error: ', error);
//     return false;
//   }
// }

// MANAGE PAUSE FUNCTION - COMMENTED OUT
// async function managePause() {
//   try {
//     if (['pause', 'hide'].includes(miscStore.pausedOffers)) {
//       await pauseOrders();
//     }
//     if (miscStore.pausedOffers === 'resume') {
//       miscStore.setResumeOffersModal();
//     }
//   } catch (error) {
//     console.log(error);
//     checkPausedOrdersError();
//   } finally {
//     emit('closePopUp');
//   }
// }

//HOOKS
// PAUSE ORDERS HOOK - COMMENTED OUT
// onMounted(async () => {
//   await checkPausedOffers();
// });
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/theme/variables.scss' as theme;

.backgroundBalance {
  background: theme.$gradient-001;
}

.log-in-link {
  color: theme.$grey-702;
  font-size: theme.$font-size-sm;
  transition: color 0.2s ease;
  border-left: 1px solid theme.$grey-701;

  &:hover {
    color: theme.$white;
  }
}

.sign-up-button {
  background: theme.$white;
  transition: color ease 0.2s;
  color: theme.$color-primary;
  text-decoration: none;

  &:hover {
    text-decoration: none;

    transform: scale(1.025);
    transition: transform 0.3s;
  }
}

button {
  flex: 1 0 auto;
  font-weight: theme.$font-weight-light;
  padding: 0.5rem 2rem;
  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    font-size: 1rem;
    padding: 0.5rem 1rem;
  }
}

.account-name {
  color: theme.$grey-300;
}

.account-balance {
  font-size: theme.$font-size-xs;
  color: theme.$grey-703;

  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    font-size: theme.$font-size-xs;

    .account-balance {
      font-size: 0.6rem;
      padding-top: 5px;

      @media screen and (max-width: 370px) {
        font-size: 0.5rem;
      }
    }
  }
}

.account-logo {
  color: theme.$white;
  font-size: 0.85rem;
  cursor: pointer;
  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    font-size: 0.8rem;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    font-size: 0.75rem;
  }
  @media screen and (max-width: 370px) {
    font-size: 0.65rem;
  }
}

.popup {
  position: absolute;
  right: 50px;
  top: 0px;
  width: 300px;
  background: theme.$white_02;
  border: 1px solid var(--grey-1200-1);
  box-shadow: 0 2px 10px theme.$black-101;
  padding: 1rem 0;
  border-radius: 10px;
  color: theme.$grey-2300;
  z-index: 2500;

  // Desktop: the gradient header carries its own 10px top radii — the same
  // value as this panel's — so the two arcs sat concentric with the panel's
  // 1px border between them, leaving a sliver of white panel background
  // visible around the header. Clip children to this radius instead and let
  // the header square its own corners (below), so the gradient reaches the
  // panel edge. Also soften the grey border, which read as a hard outline.
  // The theme attribute is set on <body> (see stores/user.js), not <html>.
  @media screen and (min-width: map.get(theme.$grid-breakpoints, md)) {
    overflow: hidden;

    body:not([theme='night']) & {
      border-color: rgba(0, 0, 0, 0.06);
      box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
    }
  }

  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    right: 25px;
    top: 20px;
    width: 250px;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    right: 5px;
  }
}

.popup-header {
  height: 75px;
  font-weight: theme.$font-weight-normal;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  color: theme.$blue-104;
  border-bottom: theme.$border-14;

  // Desktop: .popup clips to its own radius, so the header's matching radii
  // only pull the gradient inward and expose the panel background behind it.
  // Square them and let the parent do the rounding.
  @media screen and (min-width: map.get(theme.$grid-breakpoints, md)) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  .wrapper {
    width: 100%;
    margin: 0;
  }
}

.popup-menu {
  padding-block: 0.8rem;
  a,
  div {
    text-decoration: none;
    font-size: theme.$font-size-sm;
    font-weight: theme.$font-weight-normal;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.6rem 1.5rem;
    color: theme.$grey-2300;
  }

  &:hover {
    //background: theme.$color-primary;
    color: theme.$white; //#343B86
    font-weight: theme.$font-weight-semibold;
  }
}

.router-link-style {
  cursor: pointer;

  span {
    &.text-blue {
      color: theme.$color-primary_06;
    }
  }

  &:hover {
    font-weight: theme.$font-weight-semibold;
    background: theme.$color-primary;
    color: theme.$white;

    .icon-size,
    .icon-size-blue,
    .icon-size-red {
      color: theme.$white;
    }

    span {
      color: theme.$white;
    }
  }
}

.icon-size {
  width: 17px;
  height: 17px;

  &-blue {
    color: theme.$color-primary_06;
  }

  &-red {
    color: #dc3545;
  }
}

.text-red {
  color: #dc3545;
}

.mobile-logout {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding-top: 12px;
}

.column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  h3 {
    font-size: 1.125rem;
  }
}
.with-right-border {
  border-right: 1px solid theme.$blue-200;
}
.font-white {
  color: theme.$white;
}

// Location and Size of alerts/notifications relative to screen. If you change this you should also change notification.vue size.

.notifications-wrapper {
  position: fixed;
  right: 2%;
  top: 5%;
  z-index: 9999;
  @media screen and (max-width: map.get(theme.$grid-breakpoints, md)) {
    right: 20%;
    top: 20px;
    width: 250px;
  }
  @media screen and (max-width: map.get(theme.$grid-breakpoints, sm)) {
    right: 6%;
  }
}
.margin-zero {
  margin: 0px 0px 0px 25px !important;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.mobile-popup-1 {
  // Anchor the popup's bottom to the top edge of the nav bar instead of a
  // fixed top offset (which left a height-dependent gap). The bar (.menu) is
  // the positioning context and has 10px padding, so the absolute reference
  // sits 10px inside the bar — offset by that 10px so the popup ends flush
  // with the bar's top edge rather than overlapping it. Also slides up on open.
  top: auto;
  bottom: calc(100% + 10px);
  transform-origin: bottom center;
  animation: mobile-popup-slide-up 0.22s ease-out;
}

@keyframes mobile-popup-slide-up {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
